import { Component, OnInit, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionService } from '@app/core/auth/permission.service';
import { FeatureAttribute } from '@app/data/models/common/feature.model';
import { DatabaseCacheService } from '@app/data/cache/database-cache.service';
import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';

type UnauthorizedReason = 'role' | 'feature' | 'contract' | 'unknown';

@Component({
  selector: 'app-unauthorized',
  templateUrl: './unauthorized.component.html',
  styleUrls: ['./unauthorized.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: SHARED_IMPORTS,
})
export class UnauthorizedComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permission = inject(PermissionService);
  private readonly cacheService = inject(DatabaseCacheService);
  private readonly cdr = inject(ChangeDetectorRef);

  reason: UnauthorizedReason = 'unknown';
  feature: FeatureAttribute | null = null;
  sectionId: string | null = null;
  sectionName: string | null = null;
  hasFeatureInOtherGroup = false;
  isAdminContext = false;

  ngOnInit(): void {
    // Get reason from query params
    this.route.queryParams.subscribe((params) => {
      this.reason = (params['reason'] as UnauthorizedReason) ?? 'unknown';
      this.feature = (params['feature'] as FeatureAttribute) ?? null;

      // Check if feature is available in other section groups
      if (this.feature) {
        this.hasFeatureInOtherGroup =
          this.permission.hasFeatureInAnySectionGroup(this.feature);
      }

      // Trigger change detection for OnPush strategy
      this.cdr.markForCheck();
    });

    // Get sectionId from parent route
    this.route.parent?.params.subscribe((params) => {
      this.sectionId = params['sectionId'] ?? null;
      this.cdr.markForCheck();
    });

    // Detect if we're in section-admin context
    this.isAdminContext = this.router.url.includes('/section-admin');

    // Get section name for contract error display
    const selectedSection = this.cacheService.getSelectedSection();
    if (selectedSection) {
      this.sectionName = selectedSection.sectionName;
    }
  }

  get backLink(): string[] {
    return this.sectionId ? ['/home', this.sectionId, 'users'] : ['/'];
  }

  get loginLink(): string[] {
    return ['/'];
  }

  /**
   * Get link to switch to a different section (for contract expiration in admin context)
   */
  get switchSectionLink(): string[] {
    // Get sections with active contract
    const sectionsWithRole = this.cacheService.getSectionWithRole();
    const sectionDocuments = this.cacheService.getSectionDocuments();

    if (sectionsWithRole && sectionDocuments) {
      // Find first section with active contract (not CLOSE)
      const activeSection = sectionsWithRole.find((swr: { sectionId: string }) => {
        const doc = sectionDocuments.find((d: { id: string }) => d.id === swr.sectionId);
        return doc?.contractInfo?.contractStatus !== 'CLOSE';
      });

      if (activeSection) {
        return this.isAdminContext
          ? ['/section-admin', activeSection.sectionId, 'home']
          : ['/home', activeSection.sectionId, 'users'];
      }
    }

    return ['/'];
  }

  get hasActiveSection(): boolean {
    const sectionsWithRole = this.cacheService.getSectionWithRole();
    const sectionDocuments = this.cacheService.getSectionDocuments();

    if (!sectionsWithRole || !sectionDocuments) {
      return false;
    }

    return sectionsWithRole.some((swr: { sectionId: string }) => {
      const doc = sectionDocuments.find((d: { id: string }) => d.id === swr.sectionId);
      return doc?.contractInfo?.contractStatus !== 'CLOSE';
    });
  }
}
